import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Backdrop, CircularProgress, Container, Grid, Snackbar } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import backdropStyles from '../AgentSkillsManagement/styles.module.css'
import styles from './styles.module.css'
import { BlockDetail, BlockedNumber } from "../../models/RobocallBlocks/BlockedNumber"
import { RobocallBlocksActions } from "../../actions/RobocallBlocksActions"
import ActionsList from "./History"
import {
    BusinessesSample, EvidenceSummary, KeyValue, ScoreBadge, StatusBadge, VetoBadge,
} from "./Badges"
import CommentDialog, { DialogTone } from "./CommentDialog"
import ExpiryDialog from "./ExpiryDialog"
import { BlueInverseBtn, GreenBtn, GreyInverseBtn, RedBtn } from "../Styled/StyledButtons"

/**
 * One number: state, evidence, whether it is live in Redis right now, and its full
 * history.
 *
 * `live_in_cache` is worth its own line. The row says what MySQL believes; Redis is what
 * the inbound webhook actually consults. A disagreement means the reconcile job has not
 * caught up, and it is the first thing to check when somebody asks why a number is still
 * getting through.
 */
const NumberDetail = () => {
    const { phone } = useParams<{ phone: string }>()
    const [detail, setDetail] = useState<BlockDetail | null>(null)
    const [backdrop, setBackdrop] = useState(true)
    const [minSamples, setMinSamples] = useState(3)
    const [alert, setAlert] = useState<{ severity: 'error' | 'success', text: string } | null>(null)
    const [showExpiry, setShowExpiry] = useState(false)
    const [dialog, setDialog] = useState<{
        title: string
        description?: React.ReactNode
        commentRequired: boolean
        actionLabel: string
        tone: DialogTone
        submit: (comment: string) => void
    } | null>(null)

    const load = () => {
        RobocallBlocksActions.getBlock(phone)
            .then((data: any) => {
                setDetail(data)
                setBackdrop(false)
            })
            .catch((error) => {
                setBackdrop(false)
                setAlert({ severity: 'error', text: describe(error) })
            })
    }

    useEffect(() => {
        load()
        RobocallBlocksActions.getStats(1)
            .then((data: any) => {
                if (data?.config?.min_duration_samples) {
                    setMinSamples(data.config.min_duration_samples)
                }
            })
            .catch(() => { /* not essential */ })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phone])

    const describe = (error: any): string => {
        const data = error?.response?.data
        if (data?.error) { return data.error }
        if (data?.message) { return data.message }
        return error?.message || 'Request failed'
    }

    const number: BlockedNumber | null = detail?.number || null
    const after = (text: string) => {
        setAlert({ severity: 'success', text })
        load()
    }
    const fail = (error: any) => setAlert({ severity: 'error', text: describe(error) })

    const confirm = () => setDialog({
        title: `Confirm ${number!.phone_e164} is a robocall?`,
        description: number!.veto_strength > 0
            ? <><b>This number has real-lead evidence against it</b> (veto ×{number!.veto_strength}).
                Automation refuses to block it; you are overriding that.</>
            : <>It will start being rejected immediately.</>,
        commentRequired: false,
        actionLabel: 'Confirm robocall',
        tone: 'confirm',
        submit: (comment) => RobocallBlocksActions.promote(number!.id!, comment || 'Confirmed in the review queue.')
            .then(() => after('Now blocking.'))
            .catch(fail),
    })

    const clear = () => setDialog({
        title: number!.status === 'active'
            ? `Unblock ${number!.phone_e164}?`
            : `Mark ${number!.phone_e164} as NOT a robocall?`,
        description: <>Takes effect on the next call, and veto-protects the number so the
            scheduled scan cannot block it again.</>,
        commentRequired: true,
        actionLabel: number!.status === 'active' ? 'Unblock' : 'Not a robocall',
        tone: 'clear',
        submit: (comment) => RobocallBlocksActions.release(number!.id!, comment)
            .then(() => after('Cleared.'))
            .catch(fail),
    })

    return (
        <>
            <Container className={mainStyles.mainContainer} maxWidth={false}>
                {number === null ?
                    <div className={styles.Empty}>
                        {backdrop ? '' : 'This number is not known to the robocall blocker.'}
                    </div>
                    :
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <h3>{number.phone_e164}</h3>
                                        <StatusBadge status={number.status} />
                                        &nbsp;<ScoreBadge score={number.score} />
                                        <div><VetoBadge number={number} /></div>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <KeyValue label="Source">
                                            {number.source === 'agent' ? 'flagged by an agent' : number.source}
                                        </KeyValue>
                                        <KeyValue label="Blocked at">{number.blocked_at || '—'}</KeyValue>
                                        <KeyValue label="Expires">{number.expires_at || (number.status === 'active' ? 'never' : '—')}</KeyValue>
                                        <KeyValue label="Times blocked">{number.block_count}</KeyValue>
                                        <KeyValue label="Calls rejected">
                                            {number.hit_count}{number.last_hit_at ? ` (last ${number.last_hit_at})` : ''}
                                        </KeyValue>
                                    </Grid>

                                    <Grid item xs={12} md={5}>
                                        <KeyValue label="Enforced in Redis right now">
                                            <b>{detail?.enforcement.live_in_cache ? 'yes' : 'no'}</b>
                                            {detail?.enforcement.seconds_remaining !== null
                                                && detail?.enforcement.seconds_remaining !== undefined
                                                ? ` (${Math.round(detail.enforcement.seconds_remaining / 3600)}h left)`
                                                : ''}
                                        </KeyValue>
                                        {number.status === 'active' && detail?.enforcement.live_in_cache === false ?
                                            <div className={styles.Hint}>
                                                Marked active but not in the cache — the reconcile job
                                                republishes every 5 minutes.
                                            </div>
                                            : null}
                                        <EvidenceSummary evidence={number.evidence} minSamples={minSamples} />
                                        <BusinessesSample evidence={number.evidence} />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={3}>
                                        <GreyInverseBtn fullWidth={true} href="/robocall-blocks">
                                            Back to the queue
                                        </GreyInverseBtn>
                                    </Grid>
                                    {number.status === 'candidate' ?
                                        <Grid item xs={12} md={3}>
                                            <GreenBtn fullWidth={true} onClick={confirm}>Confirm robocall</GreenBtn>
                                        </Grid>
                                        : null}
                                    {number.status === 'active' ?
                                        <Grid item xs={12} md={3}>
                                            <BlueInverseBtn fullWidth={true} onClick={() => setShowExpiry(true)}>
                                                Extend / shorten
                                            </BlueInverseBtn>
                                        </Grid>
                                        : null}
                                    {(number.status === 'candidate' || number.status === 'active') ?
                                        <Grid item xs={12} md={3}>
                                            <RedBtn fullWidth={true} onClick={clear}>
                                                {number.status === 'active' ? 'Unblock' : 'Not a robocall'}
                                            </RedBtn>
                                        </Grid>
                                        : null}
                                </Grid>
                            </div>
                        </Grid>

                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Grid container spacing={4}>
                                    <Grid className={mainStyles.tableTitle} item xs={3}>Event Datetime</Grid>
                                    <Grid className={mainStyles.tableTitle} item xs={3}>Action</Grid>
                                    <Grid className={mainStyles.tableTitle} item xs={3}>Comment</Grid>
                                    <Grid className={mainStyles.tableTitle} item xs={3}>By</Grid>
                                    <ActionsList history={number.history || []} />
                                </Grid>
                            </div>
                        </Grid>
                    </Grid>
                }
            </Container>

            {dialog !== null ?
                <CommentDialog
                    show={true}
                    title={dialog.title}
                    description={dialog.description}
                    commentRequired={dialog.commentRequired}
                    actionLabel={dialog.actionLabel}
                    tone={dialog.tone}
                    toggleHandle={() => setDialog(null)}
                    onSubmit={dialog.submit}
                />
                : null}

            <ExpiryDialog
                show={showExpiry}
                target={number}
                toggleHandle={() => setShowExpiry(false)}
                onSubmit={(comment, days, permanent) =>
                    RobocallBlocksActions.changeExpiry(number!.id!, comment, days, permanent)
                        .then(() => after('Expiry updated.'))
                        .catch(fail)}
            />

            <Snackbar
                open={alert !== null}
                autoHideDuration={5000}
                onClose={() => setAlert(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setAlert(null)} severity={alert?.severity || 'error'} variant="filled">
                    <div className={backdropStyles.AlertError}>{alert?.text}</div>
                </Alert>
            </Snackbar>

            <Backdrop className={backdropStyles.Backdrop} open={backdrop}>
                <CircularProgress className={backdropStyles.BackdropProgress} />&nbsp;&nbsp;
                <p className={backdropStyles.BackdropProgress}>Loading...</p>
            </Backdrop>
        </>
    )
}

export default NumberDetail
